// Report detail. Editorial layout per kit Frame 2.
//
// Structure:
//   Crumbs: REPORTS / <CALL TYPE> · <DATE>
//   Page title with sage-em score-band + big overall score on the right
//   Two-column body: dimension bars panel | "The reading" coach blocks
//   Below: practice focus checks (the data is unchanged from /a/:id; only
//          the wrapper is restyled)
//   Below: evidence moments
//
// Voice: Supportive Candour. Status copy on focus checks is fixed
// vocabulary (improved / showed_up / absent) mapped to encouragement-
// coloured pills; never red-cross fail icons.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  getAnalysis,
  getMe,
  SessionExpiredError,
  type AnalysisResult,
  type Dimension,
  type PracticeFocusCheck,
  type PracticeFocusItem,
} from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { PageTitle } from "../components/PageTitle";
import { DimensionBar } from "../components/DimensionBar";
import { CoachBlock } from "../components/CoachBlock";
import { overallScore, scoreLabel } from "../lib/scoring";

const DIMENSION_LABEL: Record<Dimension, string> = {
  clarity: "Clarity",
  confidence: "Confidence",
  conciseness: "Conciseness",
  listening: "Listening",
  structure: "Structure",
  pacing: "Pacing",
};

const DIMENSION_ORDER: Dimension[] = [
  "clarity",
  "confidence",
  "conciseness",
  "listening",
  "structure",
  "pacing",
];

const MOMENT_LABEL: Record<AnalysisResult["moments"][number]["label"], string> = {
  strong: "Strong moment",
  missed: "Missed opportunity",
  recovery: "Recovery",
  to_watch: "Pattern to watch",
};

const FOCUS_STATUS_LABEL: Record<PracticeFocusCheck["status"], string> = {
  showed_up: "Showed up",
  absent: "Not seen",
  improved: "Improved",
  slipped: "Slipped",
};

export function ReportDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [practiceFocus, setPracticeFocus] = useState<PracticeFocusItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  // Resolve focus_id -> label for the per-meeting focus checks. The analysis
  // payload only carries focus_id; labels live on /v1/users/me. Done as a Map
  // memo so the FocusCheckRow doesn't iterate the array on every render.
  const focusLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of practiceFocus) m.set(f.id, f.label);
    return m;
  }, [practiceFocus]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const cached = sessionStorage.getItem(`analysis:${id}`);
    if (cached) {
      try {
        setResult(JSON.parse(cached) as AnalysisResult);
      } catch {
        // fall through
      }
    }

    Promise.all([getAnalysis(id), getMe().catch(() => null)])
      .then(([r, me]) => {
        if (cancelled) return;
        setResult(r);
        setPracticeFocus(me?.practiceFocus ?? []);
        sessionStorage.setItem(`analysis:${id}`, JSON.stringify(r));
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionExpiredError) {
          navigate("/login", { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : "Could not load report");
      });

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  async function copyShareLink() {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/reports/${id}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Silent: clipboard sometimes blocked.
    }
  }

  if (error) {
    return (
      <>
        <Crumbs trail={[{ label: "Reports", to: "/reports" }, "Not found"]} />
        <PageTitle>Couldn't load this report.</PageTitle>
        <p style={{ color: "rgb(var(--color-warning))", marginBottom: 16 }}>{error}</p>
        <Link to="/reports" className="btn btn-secondary">Back to reports</Link>
      </>
    );
  }

  if (!result) {
    return (
      <>
        <Crumbs trail={[{ label: "Reports", to: "/reports" }, "Loading"]} />
        <PageTitle>Loading.</PageTitle>
      </>
    );
  }

  const overall = Math.round(overallScore(result.scores));
  const band = scoreLabel(overall);
  const callTypeLabel = humaniseCallType(result.callType);
  const dateLabel = formatLongDate(result.createdAt);

  return (
    <>
      <Crumbs
        trail={[
          { label: "Reports", to: "/reports" },
          `${callTypeLabel.toUpperCase()} · ${dateLabel.toUpperCase()}`,
        ]}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: -12,
          marginBottom: 12,
        }}
      >
        <button type="button" className="btn btn-ghost" onClick={() => void copyShareLink()}>
          {shareCopied ? "Copied" : "Share"}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled
          title="Coming soon"
        >
          Export PDF
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 32,
          alignItems: "end",
          marginBottom: 32,
        }}
      >
        <div>
          <PageTitle em={`${band}.`}>{`${callTypeLabel}.`}</PageTitle>
          <p className="lede" style={{ marginBottom: 0 }}>{result.oneLineReadout}</p>
        </div>
        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              fontSize: 64,
              lineHeight: 0.85,
              letterSpacing: "-0.05em",
              color: "rgb(var(--color-ink-strong))",
            }}
          >
            {overall}
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "rgb(var(--color-sage-deep))",
              marginTop: 4,
            }}
          >
            {band}
          </span>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
          gap: 24,
          marginBottom: 40,
        }}
      >
        <section className="panel">
          <h3>Six dimensions, in context</h3>
          {DIMENSION_ORDER.map((dim) => {
            const s = result.scores[dim];
            if (!s) return null;
            return (
              <DimensionBar key={dim} name={DIMENSION_LABEL[dim]} score={s.score} label={s.label} />
            );
          })}
        </section>

        <section className="panel">
          <h3>The reading</h3>
          <CoachBlock kind="strength" body={result.whatWorked} />
          <CoachBlock kind="risk" body={result.growthEdge} />
          <CoachBlock kind="try-next" body={result.practiceAction} />
        </section>
      </div>

      {result.practiceFocusChecks && result.practiceFocusChecks.length > 0 ? (
        <section style={{ marginBottom: 40 }}>
          <p className="frame-label">How you did on your practice focus</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {result.practiceFocusChecks.map((c) => (
              <FocusCheckRow key={c.focus_id} check={c} focusLabels={focusLabels} />
            ))}
          </div>
          <p style={{ fontSize: 13, color: "rgb(var(--color-ink-muted))", margin: "12px 0 0" }}>
            Edit your practice focus on{" "}
            <Link to="/settings" style={{ color: "rgb(var(--color-sage-deep))", textDecoration: "underline" }}>
              Settings
            </Link>
            .
          </p>
        </section>
      ) : null}

      {result.moments.length > 0 ? (
        <section style={{ marginBottom: 32 }}>
          <p className="frame-label">Moments</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {result.moments.map((m, idx) => (
              <article key={idx} className="card" style={{ padding: 18, background: "rgb(var(--color-bg-sunken))" }}>
                <div className="frame-label" style={{ marginBottom: 6, color: "rgb(var(--color-sage-deep))" }}>
                  {MOMENT_LABEL[m.label]}
                </div>
                <blockquote
                  style={{
                    margin: "0 0 8px",
                    color: "rgb(var(--color-ink))",
                    fontSize: 14.5,
                    lineHeight: 1.5,
                    paddingLeft: 12,
                    borderLeft: "2px solid rgb(var(--color-butter-deep))",
                  }}
                >
                  {`"${m.quote}"`}
                </blockquote>
                <p style={{ margin: 0, fontSize: 14, color: "rgb(var(--color-ink-muted))", lineHeight: 1.5 }}>
                  {m.coachingNote}
                </p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <div style={{ marginTop: 32, display: "flex", gap: 10 }}>
        <Link to="/new" className="btn btn-primary">Start another reading</Link>
        <Link to="/reports" className="btn btn-secondary">Back to reports</Link>
      </div>
    </>
  );
}

/**
 * Practice focus check row. Same data shape as the existing /a/:id rendering;
 * only the wrapper is restyled to match the new visual system.
 */
function FocusCheckRow({ check, focusLabels }: { check: PracticeFocusCheck; focusLabels: Map<string, string> }) {
  const pillStyle = pillStyleForStatus(check.status);
  const label = focusLabels.get(check.focus_id) ?? "Practice focus";
  return (
    <article
      className="card"
      style={{
        padding: 18,
        background: "rgb(var(--color-bg-sunken))",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>{label}</p>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            padding: "4px 10px",
            borderRadius: 999,
            background: pillStyle.bg,
            color: pillStyle.fg,
            whiteSpace: "nowrap",
          }}
        >
          {FOCUS_STATUS_LABEL[check.status]}
        </span>
      </div>
      {check.evidence ? (
        <blockquote
          style={{
            margin: "0 0 10px",
            color: "rgb(var(--color-ink))",
            fontSize: 14,
            paddingLeft: 12,
            borderLeft: "2px solid rgb(var(--color-border))",
          }}
        >
          {`"${check.evidence}"`}
        </blockquote>
      ) : null}
      <p style={{ margin: 0, fontSize: 14, color: "rgb(var(--color-ink-muted))", lineHeight: 1.5 }}>
        {check.suggestion}
      </p>
    </article>
  );
}

function pillStyleForStatus(status: PracticeFocusCheck["status"]): { bg: string; fg: string } {
  if (status === "improved") {
    return {
      bg: "rgb(var(--color-sage-deep) / 0.18)",
      fg: "rgb(var(--color-sage-deep))",
    };
  }
  if (status === "showed_up") {
    return {
      bg: "rgb(var(--color-butter) / 0.6)",
      fg: "rgb(var(--color-warning))",
    };
  }
  return {
    bg: "rgb(var(--color-border))",
    fg: "rgb(var(--color-ink-muted))",
  };
}

function humaniseCallType(callType: string): string {
  switch (callType) {
    case "one_on_one": return "1:1";
    case "team_meeting": return "Team meeting";
    case "client_discovery": return "Client discovery";
    case "pitch": return "Pitch";
    case "standup": return "Standup";
    case "all_hands": return "All-hands";
    case "feedback": return "Feedback";
    case "interview": return "Interview";
    default: return callType.replace(/_/g, " ");
  }
}

function formatLongDate(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
